/**
 * "Make this recurring" — the schedule block at the foot of the four create
 * forms (expense, AP bill, AR invoice, journal entry). Ticking the box turns
 * the save into a recurring TEMPLATE (`POST /api/v1/recurring/…/templates`)
 * whose payload is the form's own create wire body minus its date field(s);
 * the scheduler then creates each document on its date.
 *
 * ── HOW A FORM PLUGS IN (helpers in `./recurringFormFields.ts`) ──────────────
 *  1. Spread `recurringFormFields` into the form's base `z.object`
 *     (BEFORE the trailing `.refine()`s) and chain
 *     `.superRefine(recurringFormRefinement(() => context))` last — the
 *     context-dependent rules (org-today, due days, a locked start) live
 *     there; the structural ones ride in `recurrence` itself. The journal
 *     modal has no resolver and calls `validateRecurrence` in `onSubmit`.
 *  2. Add `recurring_enabled: false, recurrence: undefined` to `defaultValues`
 *     — or, for `defaultRecurring` / template-edit mode,
 *     `resolveRecurringDefaults` — mount-time writes via `setValue` would
 *     flip `isDirty` and arm the host's "Discard changes?" prompt.
 *  3. Render `<RecurringSection …/>` as the LAST block of the `space-y-8`
 *     column (only `DangerZone` may follow, in template-edit mode), gated on
 *     the host's `allowRecurring` prop. The section reads the form through
 *     `useFormContext`, so it sits anywhere inside `<Form {...form}>`.
 *  4. While `recurring_enabled` is on, disable the document's date field(s)
 *     with the "Set by the schedule below" hint — they KEEP their seeded
 *     values so the base schema still passes, and `buildRecurringPayload`
 *     strips them from the body.
 *
 * ── CHROME ───────────────────────────────────────────────────────────────────
 * The AP/AR "Advanced VAT options" collapsible for the shell (bordered
 * `Collapsible` › chevron + title + `Repeat` glyph › the `self_billing`-shaped
 * checkbox row). Once ticked, the schedule is laid out in the modal's own
 * "document metaphor" voice (`docs/FULLSCREEN_FORM_MODALS.md`): ONE theme-aware
 * `bg-muted` band (`rounded-lg` — it sits outside the paper Card, so no `-mx-6`
 * bleed; theme-aware because every input inside is a shared component), a thin
 * `text-2xl font-extralight` headline carrying `describeRecurrence` ("Every
 * month on the 1st · due 30 days after"), and uppercase micro section heads
 * SCHEDULE / ENDS / NEXT DATES. Labels `text-sm font-normal`, inline
 * `text-xs text-destructive` errors (no `bg-white/90` backing — that is for the
 * frozen paper band only), `text-xs text-muted-foreground` hints; numeric
 * controls sized to their content (`w-24`). The standing notes are one muted
 * line under the band — never an `Alert`.
 *
 * ── ONE FIELD FOR THE DAY ────────────────────────────────────────────────────
 * There is no `day_of_month` / `weekday` field: the DB derives `anchor_day`
 * from `start_date`, so the "On day" / weekday control EDITS `start_date`
 * (`withDayOfMonth` / `withWeekday`) and reads back from it. Errors on
 * `recurrence.*` are i18n KEYS (`recurring.form.errors.*`) — rendered with
 * `t()` here, unlike the forms' own English messages.
 */
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ChevronRight, Repeat } from 'lucide-react';
import type { RecurringDocumentType, RecurringTemplateBaseWire } from '@releve/shared';
import { RECURRING_DOCUMENTS_FEATURE, type RecurringFormFields } from './recurringFormFields';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { DatePicker } from '@/components/ui/date-picker';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import { useFeatureEnabled } from '@/hooks/useOrganizationFeatures';
import { formatLocalISODate, parseLocalISODate } from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import {
  RECURRENCE_END_MODES,
  RECURRING_FREQUENCIES,
  RECURRING_TEXT_TOKENS_HINT_VALUE,
  dayOfMonthOf,
  defaultRecurrenceValues,
  describeRecurrence,
  maxIntervalCountFor,
  requiresDueDays,
  suggestTemplateName,
  weekdayLabels,
  weekdayOf,
  withDayOfMonth,
  withWeekday,
  type RecurringFrequency,
} from '@/lib/recurrence';
import { RecurringNextDatesPreview } from './RecurringNextDatesPreview';

export type { RecurringFormFields } from './recurringFormFields';

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

/** What the form hands the section to seed the schedule the moment the box is ticked. */
export interface RecurrenceSeed {
  /** The form's document date — the start seeds from it, clamped to org-today. */
  documentDate?: string | null;
  /** Payee / customer / description — the template-name base. */
  nameBase?: string | null;
  /** AP/AR: `dueDaysFromTerms(...)` off the form's terms; null → 30. */
  dueDays?: number | null;
}

export interface RecurringSectionProps {
  documentType: RecurringDocumentType;
  /** {@link todayInOrgZone} — the form and the DB must agree on "today". */
  orgToday: string;
  /** Read at tick time (a function, so it sees the form's CURRENT values). */
  getSeed: () => RecurrenceSeed;
  /**
   * Template-edit mode: the box is ticked and locked, and once the schedule
   * has run the start (and so the day of month) is read-only.
   */
  template?: RecurringTemplateBaseWire | null;
  /** Fires after the user ticks/unticks — hosts swap the footer label, the AP form seeds its bill number. */
  onEnabledChange?: (enabled: boolean) => void;
  /** Open the collapsible on mount (defaults to whether the box starts ticked). */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Self-gated on the `recurring_documents` flag: renders nothing (no hooks
 * beyond the flag read) when the org does not have the feature.
 */
export function RecurringSection(props: RecurringSectionProps) {
  const { enabled: featureEnabled } = useFeatureEnabled(RECURRING_DOCUMENTS_FEATURE);
  if (!featureEnabled) return null;
  return <RecurringSectionBody {...props} />;
}

function RecurringSectionBody({
  documentType,
  orgToday,
  getSeed,
  template = null,
  onEnabledChange,
  defaultOpen,
  className,
}: RecurringSectionProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;
  const form = useFormContext<RecurringFormFields>();
  const { control, setValue, getValues, clearErrors } = form;

  const enabled = useWatch({ control, name: 'recurring_enabled' }) ?? false;
  const recurrence = useWatch({ control, name: 'recurrence' });

  const [open, setOpen] = useState<boolean>(() => defaultOpen ?? Boolean(getValues('recurring_enabled')));
  const sectionId = useId();

  const isTemplate = template != null;
  const startLocked = isTemplate && template.occurrences_generated > 0;
  const doc = t(`recurring.words.${documentType}`, { count: 1 });

  // Stable across renders so a memoized child never re-renders for it.
  const getSeedRef = useRef(getSeed);
  getSeedRef.current = getSeed;

  const handleToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        const seed = getSeedRef.current();
        const values = defaultRecurrenceValues({
          orgToday,
          documentDate: seed.documentDate,
          documentType,
          dueDays: seed.dueDays,
          name: suggestTemplateName(seed.nameBase, 'monthly', (key, vars) => String(t(key, vars))),
        });
        // A user action — dirtying here is correct (the host's "Discard
        // changes?" should arm); mount-time seeding goes through defaultValues.
        setValue('recurrence', values, { shouldDirty: true });
        setValue('recurring_enabled', true, { shouldDirty: true });
        setOpen(true);
      } else {
        clearErrors('recurrence');
        setValue('recurrence', undefined, { shouldDirty: true });
        setValue('recurring_enabled', false, { shouldDirty: true });
      }
      onEnabledChange?.(checked);
    },
    [orgToday, documentType, setValue, clearErrors, onEnabledChange, t],
  );

  const frequency: RecurringFrequency = recurrence?.frequency ?? 'monthly';
  const intervalCap = maxIntervalCountFor(frequency);
  const startDate = recurrence?.start_date ?? orgToday;
  const dayOfMonth = dayOfMonthOf(startDate);
  const weekday = weekdayOf(startDate);
  const weekdays = useMemo(() => weekdayLabels(locale), [locale]);

  // The band's headline — the same sentence the grid's "Repeats" column shows.
  const words = useMemo(() => {
    if (!enabled || !recurrence) return null;
    const intervalCount = recurrence.interval_count;
    if (!parseLocalISODate(startDate) || !Number.isInteger(intervalCount) || (intervalCount ?? 0) < 1) return null;
    return describeRecurrence(
      {
        frequency,
        interval_count: intervalCount as number,
        start_date: startDate,
        due_days: requiresDueDays(documentType) ? (recurrence.due_days ?? null) : null,
      },
      (key, vars) => String(t(key, vars)),
      locale,
    );
  }, [enabled, recurrence, frequency, startDate, documentType, t, locale]);

  // The month the "On day" control edits within — see its onFocus.
  const dayBaseRef = useRef<string | null>(null);

  const setStart = useCallback(
    (iso: string) => setValue('recurrence.start_date', iso, { shouldDirty: true, shouldValidate: true }),
    [setValue],
  );

  // `recurring.form.errors.*` messages are keys; only `intervalMax` interpolates {{max}}.
  const renderError = (message: string | undefined) => {
    if (!message) return null;
    const text = message === 'recurring.form.errors.intervalMax' ? t(message, { max: intervalCap }) : t(message);
    return <span className="text-xs text-destructive">{text}</span>;
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn('border rounded-lg', className)}
      data-testid="recurring-section"
    >
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 hover:bg-muted/50 transition-colors">
        <ChevronRight className={`h-4 w-4 transition-transform ${open ? 'rotate-90' : ''}`} />
        <span className="text-sm font-medium">{t('recurring.form.sectionTitle')}</span>
        <Repeat className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 pb-4 space-y-4">
        <FormField
          control={control}
          name="recurring_enabled"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  disabled={isTemplate}
                  onCheckedChange={(checked) => handleToggle(checked === true)}
                />
              </FormControl>
              <div className="space-y-1 leading-none">
                <FormLabel className="text-sm font-normal">{t(`recurring.form.enable.${documentType}`)}</FormLabel>
                <p className="text-xs text-muted-foreground">{t('recurring.form.enableHint', { doc })}</p>
              </div>
            </FormItem>
          )}
        />

        {enabled && recurrence && (
          <>
            {/* The schedule: one theme-aware band in the modal's document voice. */}
            <div className="rounded-lg bg-muted px-6 py-6 space-y-6">
              {words && (
                <p className="text-2xl font-extralight leading-tight" data-testid="recurring-headline">
                  {words}
                </p>
              )}

              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">{t('recurring.form.scheduleHeading')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Template name */}
              <FormField
                control={control}
                name="recurrence.name"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="text-sm font-normal">{t('recurring.form.name')}</FormLabel>
                      {renderError(fieldState.error?.message)}
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={t('recurring.form.namePlaceholder')}
                        className={fieldState.error ? 'border-destructive' : ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Repeats */}
              <FormField
                control={control}
                name="recurrence.frequency"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="text-sm font-normal">{t('recurring.form.repeats')}</FormLabel>
                      {renderError(fieldState.error?.message)}
                    </div>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value as RecurringFrequency);
                        // The interval cap depends on the frequency — re-check it.
                        void form.trigger('recurrence.interval_count');
                      }}
                    >
                      <FormControl>
                        <SelectTrigger
                          className={fieldState.error ? 'border-destructive' : ''}
                          aria-label={t('recurring.form.repeats')}
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {RECURRING_FREQUENCIES.map((value) => (
                          <SelectItem key={value} value={value}>
                            {t(`recurring.frequency.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              {/* Every N <unit> */}
              <FormField
                control={control}
                name="recurrence.interval_count"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="text-sm font-normal">{t('recurring.form.every')}</FormLabel>
                      {renderError(fieldState.error?.message)}
                    </div>
                    <div className="flex items-center gap-2">
                      <FormControl>
                        <NumberInput
                          value={field.value ?? ''}
                          onChange={(value) => field.onChange(value === '' ? undefined : Number(value))}
                          onBlur={field.onBlur}
                          step={1}
                          defaultOnBlur={1}
                          className={`w-24 ${fieldState.error ? 'border-destructive' : ''}`}
                          aria-label={t('recurring.form.every')}
                        />
                      </FormControl>
                      <span className="text-sm text-muted-foreground">
                        {t(`recurring.units.${frequency}`, { count: field.value ?? 1 })}
                      </span>
                    </div>
                  </FormItem>
                )}
              />

              {/* On day (month-based) / On weekday (weekly) — both edit start_date */}
              {frequency === 'weekly' ? (
                // Not a form field of its own (it edits start_date), so plain
                // Label + control rather than FormItem/FormLabel, which expect a FormField.
                <div className="space-y-2">
                  <Label htmlFor={`${sectionId}-weekday`} className="text-sm font-normal">{t('recurring.form.onWeekday')}</Label>
                  <Select
                    value={weekday == null ? undefined : String(weekday)}
                    disabled={startLocked}
                    onValueChange={(value) => setStart(withWeekday(startDate, Number(value), orgToday))}
                  >
                    <SelectTrigger id={`${sectionId}-weekday`} className="w-40" aria-label={t('recurring.form.onWeekday')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekdays.map((label, index) => (
                        <SelectItem key={index} value={String(index)}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor={`${sectionId}-day`} className="text-sm font-normal">{t('recurring.form.onDay')}</Label>
                  <NumberInput
                    id={`${sectionId}-day`}
                    value={dayOfMonth ?? ''}
                    disabled={startLocked}
                    // Base month captured on focus so typing "2" then "5" lands on
                    // the 25th of the SAME month the user started from (a "2" that
                    // rolled into next month would otherwise drag the 25th along).
                    onFocus={() => {
                      dayBaseRef.current = startDate;
                    }}
                    onBlur={() => {
                      dayBaseRef.current = null;
                    }}
                    onChange={(value) => {
                      if (value === '') return;
                      const day = Number(value);
                      if (Number.isInteger(day) && day >= 1 && day <= 31) {
                        setStart(withDayOfMonth(dayBaseRef.current ?? startDate, day, startLocked ? undefined : orgToday));
                      }
                    }}
                    step={1}
                    allowEmpty
                    className="w-24"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('recurring.form.dayOfMonthHint')}</p>
                </div>
              )}

              {/* Starts */}
              <FormField
                control={control}
                name="recurrence.start_date"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <div className="flex items-center gap-2">
                      <FormLabel className="text-sm font-normal">{t('recurring.form.starts')}</FormLabel>
                      {renderError(fieldState.error?.message)}
                    </div>
                    <FormControl>
                      <DatePicker
                        value={parseLocalISODate(field.value)}
                        onChange={(date) => field.onChange(formatLocalISODate(date) ?? '')}
                        // Create mode only: a never-run template may carry an unchanged past
                        // start, which the schema allows and a native `min` would refuse.
                        min={isTemplate ? undefined : (parseLocalISODate(orgToday) ?? undefined)}
                        disabled={startLocked}
                        className={fieldState.error ? 'border-destructive' : ''}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground mt-1">
                      {startLocked ? t('recurring.form.startsLockedHint') : t('recurring.form.startsHint', { doc })}
                    </p>
                  </FormItem>
                )}
              />

              {/* Due (AP / AR) */}
              {requiresDueDays(documentType) && (
                <FormField
                  control={control}
                  name="recurrence.due_days"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-sm font-normal">{t('recurring.form.dueDays')}</FormLabel>
                        {renderError(fieldState.error?.message)}
                      </div>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <NumberInput
                            value={field.value ?? ''}
                            allowEmpty
                            onChange={(value) => field.onChange(value === '' ? undefined : Number(value))}
                            onBlur={field.onBlur}
                            step={1}
                            className={`w-24 ${fieldState.error ? 'border-destructive' : ''}`}
                            aria-label={t('recurring.form.dueDays')}
                          />
                        </FormControl>
                        <span className="text-sm text-muted-foreground">
                          {t(`recurring.form.dueDaysSuffix.${documentType}`)}
                        </span>
                      </div>
                    </FormItem>
                  )}
                />
              )}

                </div>
              </div>

              {/* Ends — never / on a date / after N */}
              <FormField
                control={control}
                name="recurrence.end_mode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 block">{t('recurring.form.ends')}</FormLabel>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        clearErrors(['recurrence.end_date', 'recurrence.max_occurrences']);
                      }}
                      className="flex flex-col gap-3 md:flex-row md:items-start md:gap-6"
                    >
                      {RECURRENCE_END_MODES.map((mode) => (
                        <div key={mode} className="flex items-start gap-2">
                          <RadioGroupItem value={mode} id={`${sectionId}-end-${mode}`} className="mt-0.5" />
                          <div className="space-y-2">
                            <Label htmlFor={`${sectionId}-end-${mode}`} className="text-sm font-normal cursor-pointer">
                              {t(`recurring.endModes.${mode}`)}
                            </Label>
                            {mode === 'on_date' && field.value === 'on_date' && (
                              <FormField
                                control={control}
                                name="recurrence.end_date"
                                render={({ field: endField, fieldState }) => (
                                  <FormItem>
                                    {renderError(fieldState.error?.message)}
                                    <FormControl>
                                      <DatePicker
                                        value={parseLocalISODate(endField.value)}
                                        onChange={(date) => endField.onChange(formatLocalISODate(date) ?? '')}
                                        min={parseLocalISODate(startDate) ?? undefined}
                                        className={fieldState.error ? 'border-destructive' : ''}
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            )}
                            {mode === 'after_count' && field.value === 'after_count' && (
                              <FormField
                                control={control}
                                name="recurrence.max_occurrences"
                                render={({ field: countField, fieldState }) => (
                                  <FormItem>
                                    {renderError(fieldState.error?.message)}
                                    <div className="flex items-center gap-2">
                                      <FormControl>
                                        <NumberInput
                                          value={countField.value ?? ''}
                                          allowEmpty
                                          onChange={(value) =>
                                            countField.onChange(value === '' ? undefined : Number(value))
                                          }
                                          onBlur={countField.onBlur}
                                          step={1}
                                          className={`w-24 ${fieldState.error ? 'border-destructive' : ''}`}
                                          aria-label={t('recurring.form.afterCount')}
                                        />
                                      </FormControl>
                                      <span className="text-sm text-muted-foreground">
                                        {t('recurring.form.afterCountSuffix')}
                                      </span>
                                    </div>
                                  </FormItem>
                                )}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormItem>
                )}
              />

              {/* AR: issue each invoice on its date, or leave it a draft (default). */}
              {documentType === 'ar_invoice' && (
                <FormField
                  control={control}
                  name="recurrence.auto_issue"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start gap-3 space-y-0">
                      <FormControl>
                        <Switch
                          checked={field.value === true}
                          onCheckedChange={(checked) => field.onChange(checked)}
                          aria-describedby={`${sectionId}-auto-issue-hint`}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">{t('recurring.form.autoIssue.label')}</FormLabel>
                        <p id={`${sectionId}-auto-issue-hint`} className="text-xs text-muted-foreground">
                          {t('recurring.form.autoIssue.hint')}
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              <RecurringNextDatesPreview
                documentType={documentType}
                frequency={frequency}
                interval_count={recurrence.interval_count}
                start_date={startDate}
                end_date={recurrence.end_mode === 'on_date' ? recurrence.end_date : null}
                max_occurrences={recurrence.end_mode === 'after_count' ? recurrence.max_occurrences : null}
                due_days={requiresDueDays(documentType) ? recurrence.due_days : null}
                orgToday={orgToday}
                template={template}
              />
            </div>

            {/* Standing notes: one muted line, each note its own span (the AP
                form carries the tokens guidance as its bill-number hint). */}
            <p className="text-xs text-muted-foreground">
              <span>{t('recurring.form.notes.notCopied')}</span>
              {documentType === 'ar_invoice' && (
                <>
                  {' · '}
                  <span>{t('recurring.form.notes.ar_invoice')}</span>
                </>
              )}
              {documentType === 'expense' && (
                <>
                  {' · '}
                  <span>{t('recurring.form.notes.expense')}</span>
                </>
              )}
              {documentType !== 'ap_bill' && (
                <>
                  {' · '}
                  <span>{t('recurring.form.tokensHint', { tokens: RECURRING_TEXT_TOKENS_HINT_VALUE })}</span>
                </>
              )}
            </p>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
